import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {Transition} from 'react-transition-group';
import {StyledExpensesPanel, StyledExpensesPanelContainer} from './StyledExpensesPanel';
import AddExpenseModal from '../AddExpenseModal';
import ExpensesTab from '../ExpensesTab';
import ExpenseDetailsPanel from '../ExpenseDetailsPanel';
import ApproveExpenseModal from '../ExpenseDetailsPanelTabs/tabComponents/ApproveExpenseModal';
import ExpenseAgentChat from '../../OverviewPage/AgentChatPanel';
import RejectExpenseModal from '../ExpenseDetailsPanelTabs/tabComponents/RejectExpenseModal';
import ExpensesWebsocketConnection from '../ExpensesWebsocketConnection';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow';
import {
  backendWebsocketActionsConstants,
  DEFAULT_MODAL_PROPS,
  DEFAULT_PAGE_SIZE, expenseReviewStatusConstants,
  SCAActionsConstants,
  SYSTEM_ANIMATION_DURATION
} from '../../../../constants';
import {useExpenseRelatedData, useIsEmployeeLoaded, useTabState} from '../../../../hooks';
import {
  accountingHelpers,
  expensesHelpers,
  objectHelpers,
  reviewHelpers,
  tabHelpers,
  transactionsHelpers,
} from '../../../../utils/helpers';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {EXPENSES_PAGE_SEARCH_KEYS} from '../../../../pages/ExpensesPage/expensesPage.constants';

const view = 'all';

const {details: detailsStyles, table: tableStyles} = transactionsHelpers.getAnimationStyles({duration: SYSTEM_ANIMATION_DURATION, detailsMaxWidth: 448});

const summaraizeKeyName = 'isMonthGrouping';

const {SELECTED_EXPENSE_ID} = EXPENSES_PAGE_SEARCH_KEYS;

const ExpensesPanel = ({ tableContainerProps, ...rest }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [t] = useTranslation(['main', 'expenses']);
  const [detailsWindowProps, setDetailsWindowProps] = useState(DEFAULT_MODAL_PROPS);
  const [approveModalProps, setApproveModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [rejectModalProps, setRejectModalProps] = useState(DEFAULT_MODAL_PROPS);

  const [searchParams, setSearchParams] = useSearchParams();

  // refs for scroll handling
  const tableRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const rafRef = useRef(null);
  const lastClassNameRef = useRef('full');
  const [detailsPanelClassName, setDetailsPanelClassName] = useState('full');

  const {
    tableData, setTableData,
    queries, setQueries,
    loading, setLoading,
  } = useTabState({defaultQuery: { order_by: '-date', per_page: DEFAULT_PAGE_SIZE }, tabKeys: [view]});

  const { isAdmin, isOpenAddExpenseModal, employeeEmail } = useSelector((state) => ({
    isAdmin: state.user?.isAdmin,
    isOpenAddExpenseModal: state?.expense.addExpenseModalProps.open,
    employeeEmail: state.user?.employee?.email,
  }));

  const isEmployeeLoaded = useIsEmployeeLoaded();

  const dispatch = useDispatch();

  const {
    subscriptions,
    transactions,
    setSubscriptions,
    loadExpenseRelatedData,
    loadExpenseTransactions,
    authWindowProps,
    handleCloseAuthModal,
  } = useExpenseRelatedData();

  const detailsWindowSubscription = useMemo(() => {
    return expensesHelpers.getExpenseRelatedObject({detailsWindowProps, data: subscriptions, key: 'subscription_id'});
  }, [detailsWindowProps?.expense?.subscription_id, detailsWindowProps?.open, subscriptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const detailsWindowTransaction = useMemo(() => {
    return expensesHelpers.getExpenseRelatedObject({detailsWindowProps, data: transactions, key: 'card_transaction_id'});
  }, [detailsWindowProps?.expense?.card_transaction_id, detailsWindowProps?.open, transactions]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = ({
    query = null,
    initial = false,
    skipLoading = false,
    updateCurrentPage = false,
    onSuccess
  }) => {
    const viewQuery = tabHelpers.getViewQuery({
      initial, query, queries, view, updateCurrentPage, tableData
    });

    setQueries({ ...queries, [view]: viewQuery});

    const finishTableLoading = () => {
      setLoading(prevState => ({...prevState, [view]: false}));
    }

    if (!skipLoading) {
      setLoading(prevState => ({...prevState, [view]: true}));
    }

    dispatch(expenseActions.getExpenses({
      query: getQuery(viewQuery),
      successCallback: (response) => {
        response.results = expensesHelpers.getGroupedExpenses(response.results);

        if (!initial) {
          const combinedRows = [...tableData[view].rows, ...response.results].filter(row => !row.isMonthGrouping);
          response.results = expensesHelpers.getGroupedExpenses(combinedRows);
        }

        tabHelpers.handleStoreTableDataWithPrev({
          tableData: initial ? tableData : { ...tableData, [view]: { ...tableData[view], rows: [] } },
          setTableData,
          view,
          response,
          initial,
          updateCurrentPage
        });

        !skipLoading && finishTableLoading();
        onSuccess?.();
      },
      errorCallback: () => !skipLoading && finishTableLoading()
    }));
  }

  const loadExpenseData = ({id, successCallback, errorCallback, isUpdate = true} = {}) => {
    if (!id) return;
    dispatch(expenseActions.getExpense({
      id,
      successCallback: (expense) => {
        setTableData(prevState => ({
          ...tableData,
          [view]: {
            ...prevState[view],
            rows:
              isUpdate
                ? (tableData[view]?.rows || []).map(exp => exp.id === expense.id ? expense : exp)
                : [...(tableData[view]?.rows || []), expense],
            loaded: true
          }
        }));
        successCallback?.(expense);
      },
      errorCallback
    }));
  }

  const handleOnTableChange = (pagination, filters, sorter, extra) => {
    tabHelpers.handleOnTableChange({view, sorter, extra, loadDataFunc: loadData});
  }

  const handleOnFilter = (filterQuery) => {
    tabHelpers.handleOnFilter({view, filterQuery, loadDataFunc: loadData, queries, setQueries});
    setSearchParams(
      objectHelpers.getSearchQueryParams({searchParams, filterQuery})
    );
  }

  // handle scroll to update details panel top position
  useEffect(() => {
    scrollContainerRef.current = document.getElementById('app-page-container');
    const scrollContainer = scrollContainerRef.current;

    const updateDetailsPanelClassName = () => {
      if (!tableRef.current) return;

      const tableOffsetTop = tableRef.current?.getBoundingClientRect().top || 0;
      const nextClassName = tableOffsetTop <= 90 ? 'full' : 'short';
      if (nextClassName !== lastClassNameRef.current) {
        lastClassNameRef.current = nextClassName;
        setDetailsPanelClassName(nextClassName);
      }
    };

    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        updateDetailsPanelClassName();
      });
    };

    updateDetailsPanelClassName();
    scrollContainer?.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      scrollContainer?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [tableRef.current, isEmployeeLoaded, detailsWindowProps.open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const query = objectHelpers.removeKeysFromObject(
      objectHelpers.getSearchParams(searchParams)
    );
    loadData({initial: true, query});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!!detailsWindowProps.expense) return;

    const selectedExpenseId = new URLSearchParams(location.search).get(SELECTED_EXPENSE_ID);
    if (!selectedExpenseId) return;

    const expense = tableData[view]?.rows?.find(e => e.id === selectedExpenseId);
    if (expense) {
      handleOnRowClick(undefined, expense);
    } else {
      const loaded = tableData[view].loaded;
      if (loaded) {
        loadExpenseData({
          id: selectedExpenseId,
          successCallback: () => handleOnRowClick(undefined, expense),
          isUpdate: false
        });
      }
    }
  }, [tableData]); // eslint-disable-line react-hooks/exhaustive-deps

  const getQuery = (query) => {
    const review_status = query.review_status;
    let statusesEnum = {
      requested: [expenseReviewStatusConstants.IN_REVIEW],
      reviewed: [expenseReviewStatusConstants.APPROVED, expenseReviewStatusConstants.REJECTED],
    };
    return {
      ...query,
      review_status: statusesEnum[review_status] || undefined,
    }
  }

  const handleOnRowClick = (e, expense) => {
    if (!expense || expense[summaraizeKeyName]) return;
    setDetailsWindowProps(prevState => {
      const open = expense?.id === prevState.expense?.id ? !prevState.open : true;
      return { ...prevState, open, expense, index: open ? expense?.id : null }
    });

    const searchParams = new URLSearchParams(location.search);
    if (expense.id !== detailsWindowProps.expense?.id) {
      searchParams.set(SELECTED_EXPENSE_ID, expense?.id);
    } else {
      searchParams.delete(SELECTED_EXPENSE_ID);
    }

    loadExpenseRelatedData(expense);

    navigate({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
      hash: location.hash,
    });
  }

  const handleCloseApproveModal = () => setApproveModalProps({...approveModalProps, open: false});

  const handleCloseRejectModal = () => setRejectModalProps({...rejectModalProps, open: false});

  const handleOpenRejectModal = ({e, expense, data = {}}) => {
    e?.stopPropagation();
    setRejectModalProps({
      ...DEFAULT_MODAL_PROPS,
      open: true,
      expense,
      data
    });
  }

  const updateTableAndDetails = ({id, response, forceUpdate = false} = {}) => {
    const expenseFromTable = tableData[view].rows.find(e => e.id === id);
    if (forceUpdate || (expenseFromTable && expenseFromTable?.review_status !== response?.review_status)) {
      loadData({
        initial: true,
        onSuccess: () => {
          loadData({initial: true, skipLoading: true});
        }
      });
    } else {
      setTableData(prevState => ({
        ...prevState,
        [view]: {
          ...prevState[view],
          rows: prevState[view].rows.map(exp => exp.id === id ? response : exp)
        }
      }));
    }

    if (detailsWindowProps.open && detailsWindowProps?.expense?.id === id) {
      setDetailsWindowProps(prevState => ({ ...prevState, expense: response }));
    }
  };

  const handleOkRejectModal = ({fields, successCallback, errorCallback}) => {
    const id = rejectModalProps?.expense?.id;
    if (!id) return;

    setRejectModalProps(prevState => ({ ...prevState, loading: true }));

    dispatch(expenseActions.rejectExpense({
      id,
      data: {
        ...fields,
        ...(rejectModalProps?.data || {})
      },
      successCallback: (response) => {
        setRejectModalProps(DEFAULT_MODAL_PROPS);
        updateTableAndDetails({id, response});
        successCallback?.(response);
      },
      errorCallback: (err) => {
        setRejectModalProps(prevState => ({ ...prevState, loading: false }));
        errorCallback?.(err?.response);
      },
    }));
  }

  const handleOkApproveModal = ({successCallback, errorCallback}) => {
    const expense = approveModalProps?.expense;
    if (!expense) return;

    setApproveModalProps(prevState => ({ ...prevState, loading: true }))
    handleApproveExpense({
      expense,
      data: { admin_override: true },
      successCallback: (response) => {
        handleCloseApproveModal();
        successCallback?.(response);
      },
      errorCallback: (response) => {
        setApproveModalProps(prevState => ({ ...prevState, loading: false }))
        errorCallback?.(response);
      }
    });
  }

  const handleApproveTableExpense = ({e, expense, data}) => {
    e?.stopPropagation();

    const canQuickApprove = reviewHelpers.canQuickApprove({data: expense, employeeEmail, isAdmin});
    if (canQuickApprove) {
      handleApproveExpense({e, expense, data});
    } else {
      setApproveModalProps({
        ...DEFAULT_MODAL_PROPS,
        open: true,
        expense,
      });
    }
  }

  const handleApproveDetailsExpense = ({e, entity, expense, data}) => {
    const action = data?.admin_override ? handleApproveTableExpense : handleApproveExpense;
    action({e, expense: expense || entity});
  }

  const handleApproveExpense = ({e, expense, data = {}, successCallback, errorCallback}) => {
    e?.stopPropagation();

    const id = expense?.id;
    if (!id) return;

    dispatch(expenseActions.approveExpense({
      id,
      data,
      successCallback: (response) => {
        updateTableAndDetails({id, response});
        successCallback?.(response);
      },
      errorCallback: (response) => {
        expensesHelpers.handleExpenseError({response, fallbackKey: 'approveExpenseError', t});
        errorCallback?.(response);
      },
    }));
  }

  const handleReviewEdit = () => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    dispatch(expenseActions.reopenExpense({
      id,
      successCallback: (response) => updateTableAndDetails({id, response, forceUpdate: true}),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'reopenExpenseError', t}),
    }));
  }

  const handleReassignExpense = (e, data) => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    dispatch(expenseActions.reassignExpense({
      id,
      data,
      successCallback: (response) => updateTableAndDetails({id, response}),
      errorCallback: (response) =>expensesHelpers.handleExpenseError({response, fallbackKey: 'reassignExpenseError', t}),
    }));
  }

  const handleDiscardAutoApproval = () => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    dispatch(expenseActions.discardExpenseAutoApproval({
      id,
      successCallback: (response) => updateTableAndDetails({id, response}),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'discardAutoApprovalError', t}),
    }));
  }

  const handleEditExpense = (data) => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    dispatch(expenseActions.updateExpense({
      id,
      data,
      successCallback: (response) => {
        setTableData(prevState => ({
          ...prevState,
          [view]: {
            ...prevState[view],
            rows: prevState[view].rows.map(exp => exp.id === id ? response : exp)
          }
        }));

        if (detailsWindowProps.open && detailsWindowProps?.expense?.id === id) {
          setDetailsWindowProps(prevState => ({
            ...prevState, expense: response
          }));
        }
      },
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'updateExpenseError', t}),
    }));
  }

  const handleUpdateExpense = (updatedExpense) => {
    const selectedExpense = detailsWindowProps?.expense;
    const id = selectedExpense?.id;
    if (!id) return;

    if (updatedExpense) {
      if (detailsWindowProps.open && detailsWindowProps?.expense?.id === id) {
        setDetailsWindowProps(prevState => ({
          ...prevState, expense: { ...prevState.expense, ...updatedExpense }
        }));
      }

      // remove or add expense to table if unmatched filter is selected
      const queryPolicyStatus = queries[view]?.policy_status;

      if (queryPolicyStatus && String(updatedExpense.policy_status) !== String(queryPolicyStatus)) {
        setTableData(prevState => ({
          ...prevState,
          [view]: {
            ...prevState[view],
            rows: prevState[view].rows.filter(i => i.id !== id)
          }
        }));
        return;
      }

      setTableData(prevState => ({
        ...prevState,
        [view]: {
          ...prevState[view],
          rows: prevState[view].rows.map(exp => exp.id === id ? {...exp, ...updatedExpense} : exp)
        }
      }));
    }
  }

  const handleInvoiceRemove = ({initialCallback, successCallback, errorCallback} = {}) => {
    transactionsHelpers.removeInvoiceFromTransaction({
      t,
      transaction: {expense: detailsWindowProps?.expense},
      initialCallback,
      successCallback,
      errorCallback,
      unlinkExpense: (props) => dispatch(invoicesActions.unlinkExpense(props)),
      onExpenseUpdate: handleUpdateExpense
    });
  }

  const handleCloseDetailsModal = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(SELECTED_EXPENSE_ID);

    navigate({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
      hash: location.hash,
    });

    setDetailsWindowProps({...detailsWindowProps, open: false, expense: null, index: null});
  }

  const handleDeleteExpense = () => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    handleCloseDetailsModal();

    setTimeout(() => {
      setTableData(prevState => ({
        ...prevState,
        [view]: {
          ...prevState[view],
          rows: prevState[view].rows.filter(i => i.id !== id)
        }
      }));
    }, 300);
  }

  const handleSuccessExpenseSubmitted = () => {
    if (tableData[view].loaded) {
      loadData({initial: true});
    }
    if (isOpenAddExpenseModal) {
      dispatch(expenseActions.closeAddExpenseModal());
    }
  }

  const handleWebsocketMessage = (data) => {
    const getResponseProp = (key) => objectHelpers.getObjProp(data, key);
    const action = getResponseProp('action');

    if (!action) return;

    ({
      [backendWebsocketActionsConstants.EXPENSE_SUBMITTED]: handleSuccessExpenseSubmitted,
      [backendWebsocketActionsConstants.EXPENSE_FILL_MISSING_INFO]: () => {
        const expenseId = getResponseProp('expense_id') || getResponseProp('expense');
        if (!expenseId) return;

        loadExpenseData({
          id: expenseId,
          successCallback: (expense) => {
            if (detailsWindowProps.open && detailsWindowProps?.expense?.id === expenseId) {
              setDetailsWindowProps({...detailsWindowProps, expense});
            }
          }
        });
      }
    })[action]?.();
  };

  const handleSubscriptionUpdate = (updatedSubscription) => {
    const subscriptionId = detailsWindowSubscription?.id;
    if (subscriptionId && updatedSubscription?.id === subscriptionId) {
      setSubscriptions(prevState => prevState.map(sub => sub.id === subscriptionId ? {...sub, ...updatedSubscription} : sub));
    }
  }

  const handleSuccessAuth = () => {
    const expense = detailsWindowProps?.expense;
    if (!expense) return;
    loadExpenseTransactions(expense);
  }

  const getLoadMoreProps = (view) => {
    const cursor = tableData[view]?.pagination?.cursor;
    return {
      cursor: cursor,
      isEnabled: Boolean(cursor),
      onClick: () => loadData({query: { cursor }})
    }
  }

  const getTableFiltersProps = () => ({
    defaultQuery: queries[view],
    filterFields: [
      'search', 'review_status', 'payment_method', 'policy_status', 'expense_type',
      ...(isAdmin ? ['user_email'] : []),
    ],
  });

  const tableProps = {
    onChange: handleOnTableChange,
    onRow: (record) => ({
      onClick: (e) => handleOnRowClick(e, record)
    }),
    pagination: false,
    rowClassName: (record) => transactionsHelpers.getRowClassName({
      groupedPropName: summaraizeKeyName,
      index: record?.id,
      record,
      selectedRowIndex: detailsWindowProps?.index,
    }),
  };

  const tabProps = {
    data: accountingHelpers.getTableDataValue(tableData, view, 'rows', []),
    tableFiltersProps: getTableFiltersProps(),
    loadMoreProps: getLoadMoreProps(view),
    loading: loading[view] || false,
    onApprove: handleApproveTableExpense,
    onReject: handleOpenRejectModal,
    onFilter: handleOnFilter,
    tableProps
  };

  return (
    <StyledExpensesPanel {...rest}>
      <Transition in={detailsWindowProps.open} timeout={SYSTEM_ANIMATION_DURATION}>
        {state => (
          <StyledExpensesPanelContainer
            style={{ ...tableStyles.default, ...tableStyles[state] }}
            ref={tableRef}
          >
            <ExpensesTab {...tabProps} />
          </StyledExpensesPanelContainer>
        )}
      </Transition>
      <Transition in={detailsWindowProps.open} timeout={SYSTEM_ANIMATION_DURATION}>
        {state => (
          <ExpenseDetailsPanel
            {...detailsWindowProps}
            className={`expense-details-panel ${detailsPanelClassName}`}
            defaultActivePanelKey={['expense']}
            onApprove={handleApproveDetailsExpense}
            onClose={handleCloseDetailsModal}
            onDelete={handleDeleteExpense}
            onDiscardAutoApproval={handleDiscardAutoApproval}
            onEdit={handleEditExpense}
            onInvoiceRemove={handleInvoiceRemove}
            onSubscriptionUpdate={handleSubscriptionUpdate}
            onUpdate={handleUpdateExpense}
            onReassign={handleReassignExpense}
            onReject={({e, data = {}}) => handleOpenRejectModal({data, e, expense: detailsWindowProps.expense})}
            onReviewEdit={handleReviewEdit}
            subscription={detailsWindowSubscription}
            transaction={detailsWindowTransaction}
            style={{ ...detailsStyles.default, ...detailsStyles[state] }}
          />
        )}
      </Transition>

      <RejectExpenseModal
        {...rejectModalProps}
        handleCancel={handleCloseRejectModal}
        handleOk={handleOkRejectModal}
      />

      <ApproveExpenseModal
        {...approveModalProps}
        handleCancel={handleCloseApproveModal}
        handleOk={handleOkApproveModal}
      />

      <ExpenseAgentChat />

      <AddExpenseModal skipClosingAfterSuccess={true} />

      <ExpensesWebsocketConnection onMessage={handleWebsocketMessage} />

      <AuthenticationWindow
        {...authWindowProps}
        authModalProps={{ title: `${t('transaction')} ${t('loading')}` }}
        handleCancel={handleCloseAuthModal}
        onSuccess={handleSuccessAuth}
        operationName={SCAActionsConstants.TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION}
      />

    </StyledExpensesPanel>
  );
}

export default ExpensesPanel;
